import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import publicationsData from '../data/publications.json'
import { TiArrowLeftThick } from 'react-icons/ti'

export const Route = createFileRoute('/publications')({
  component: Publications,
})

interface Publication {
  idStr?: string
  name: string
  date: string
  url: string
  comments?: string
}

interface Category {
  idStr?: string
  name: string
  publications?: Publication[]
  categories?: Category[]
}

interface PublicationsData {
  categories: Category[]
}

type SortMode = 'category' | 'date'

function renderPublication(pub: Publication, index: number) {
  return (
    <li key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="flex-1">
          <a
            href={pub.url}
            className="text-blue-600 hover:text-blue-800 font-medium"
            target={pub.url.startsWith('http') ? '_blank' : undefined}
            rel={pub.url.startsWith('http') ? 'noopener noreferrer' : undefined}
          >
            {pub.name}
          </a>
          {pub.comments && (
            <p className="text-sm text-gray-600 mt-1">{pub.comments}</p>
          )}
        </div>
        <span className="text-sm text-gray-500 whitespace-nowrap">{pub.date}</span>
      </div>
    </li>
  )
}

function renderCategory(category: Category, level: number = 0) {
  const headingClasses = [
    'text-2xl font-bold text-gray-900 mb-4',
    'text-xl font-bold text-gray-800 mb-3',
    'text-lg font-semibold text-gray-700 mb-2',
  ]

  return (
    <div key={category.idStr || category.name} className={level > 0 ? 'ml-6 mt-6' : 'mb-8'}>
      <h3 className={headingClasses[Math.min(level, 2)]}>{category.name}</h3>

      {category.publications && category.publications.length > 0 && (
        <ul className="space-y-4 mb-6">
          {category.publications.map((pub, idx) => renderPublication(pub, idx))}
        </ul>
      )}

      {category.categories && category.categories.length > 0 && (
        <div className="space-y-6">
          {category.categories.map(cat => renderCategory(cat, level + 1))}
        </div>
      )}
    </div>
  )
}

function flattenPublications(categories: Category[]): Publication[] {
  const publications: Publication[] = []

  function traverse(category: Category) {
    if (category.publications) {
      publications.push(...category.publications)
    }
    if (category.categories) {
      category.categories.forEach(traverse)
    }
  }

  categories.forEach(traverse)
  return publications
}

function parseDate(dateStr: string): Date {
  // Handle various date formats: "3/4/2023", "2/2018", "2013 - 2021"
  const parts = dateStr.trim().split(/[\s-]+/)
  const firstDate = parts[0]

  if (firstDate.includes('/')) {
    const [month, day, year] = firstDate.split('/').map(Number)
    return new Date(year, month - 1, day || 1)
  } else {
    // Just a year or month/year
    const slashParts = firstDate.split('/')
    if (slashParts.length === 2) {
      const [month, year] = slashParts.map(Number)
      return new Date(year, month - 1, 1)
    } else {
      const year = parseInt(firstDate)
      return new Date(year, 0, 1)
    }
  }
}

function ButtonGroup({ value, onChange }: { value: SortMode, onChange: (mode: SortMode) => void }) {
  return (
    <div className="inline-flex rounded-md shadow-sm mb-6" role="group">
      <button
        type="button"
        onClick={() => onChange('category')}
        className={`px-4 py-2 text-sm cursor-pointer font-medium rounded-l-lg border transition-colors ${value === 'category'
            ? 'bg-blue-600 text-white border-blue-600'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
      >
        By Category
      </button>
      <button
        type="button"
        onClick={() => onChange('date')}
        className={`px-4 py-2 text-sm cursor-pointer font-medium rounded-r-lg border-t border-r border-b transition-colors ${value === 'date'
            ? 'bg-blue-600 text-white border-blue-600'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
      >
        By Date
      </button>
    </div>
  )
}

function Publications() {
  const data = publicationsData as PublicationsData
  const [sortMode, setSortMode] = useState<SortMode>('category')

  const sortedPublications = sortMode === 'date'
    ? flattenPublications(data.categories).sort((a, b) => {
      return parseDate(b.date).getTime() - parseDate(a.date).getTime()
    })
    : []

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="mb-6">
          <a href="/" className="text-blue-600 hover:text-blue-800 flex items-center gap-2"><TiArrowLeftThick /> Home</a>
        </div>
        
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Publications</h2>

        <ButtonGroup value={sortMode} onChange={setSortMode} />

        {sortMode === 'category' ? (
          data.categories.map(category => renderCategory(category))
        ) : (
          <ul className="space-y-4">
            {sortedPublications.map((pub, idx) => renderPublication(pub, idx))}
          </ul>
        )}
      </div>
    </div>
  )
}
